import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import moment from 'moment';
import {
  addAdmin,
  apiFetch,
  CockpitBehoerde,
  CockpitFaellig,
  CockpitFertig,
  CockpitAbgelaufen,
  getCockpit,
  KontaktArt,
  logKontakt,
  sendeAbgelaufenAufforderung,
  addBlockiert,
  AdminEintrag,
  BlockEintrag,
  listBlockierte,
  removeBlockiert,
  AuditEintrag,
  bestaetigeFreigabeAuftrag,
  getAudit,
  antragFreigeben,
  antragUebernehmen,
  getAntragAusweis,
  getAntragDatei,
  AntragAusweis,
  downloadAntragZip,
  FreigabeAuftrag,
  freigabeAuftragJetzt,
  listAdmins,
  listAntraege,
  listFreigabeAuftraege,
  listPendingDokumente,
  downloadPendingDokument,
  setPendingDokumentStatus,
  uebernehmePendingDokument,
  PendingAntrag,
  PendingDokument,
  removeAdmin,
  setAntragStatus,
  stornoFreigabeAuftrag,
} from '../portal/ApiClient';
import { buildBoard } from './kanban/buildBoard';
import KanbanBoard from './kanban/KanbanBoard';
import { BoardActions } from './kanban/KanbanCard';
import { BoardCard } from './kanban/types';

/**
 * Admin-Queue der über das Portal eingereichten Anträge.
 *
 * Ablauf: ZIP herunterladen -> prüfen (MRZ-Regeln, Lücken) -> Daten nach USP
 * übernehmen (Import) -> hier "Freigeben" klicken. Die Freigabe läuft über
 * das Gateway mit harten Guards (Doppel-Freigabe-Sperre 10 Monate,
 * Offenliste, u_id=49, 40-Tage-Zeitfenster mit Übersteuerungs-Option).
 */
function AntraegeAdmin() {
  const { keycloak } = useKeycloak();
  const [antraege, setAntraege] = useState<PendingAntrag[]>([]);
  const [loading, setLoading] = useState(true);       // nur das Erstladen zeigt den Spinner
  const [refreshing, setRefreshing] = useState(false); // spätere load()-Aufrufe: Board bleibt stehen
  const [msg, setMsg] = useState<{ variant: 'success' | 'danger' | 'warning'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [auftraege, setAuftraege] = useState<FreigabeAuftrag[]>([]);
  const [dokumente, setDokumente] = useState<PendingDokument[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEintrag[]>([]);
  const [admins, setAdmins] = useState<AdminEintrag[]>([]);
  const [blockierte, setBlockierte] = useState<BlockEintrag[]>([]);
  const [entsperrKandidat, setEntsperrKandidat] = useState<BlockEintrag | null>(null);
  const [neuBlock, setNeuBlock] = useState('');
  const [neuBlockGrund, setNeuBlockGrund] = useState('operatives FO24-Konto');
  const [envAdmins, setEnvAdmins] = useState<string[]>([]);
  const [cockpitBehoerde, setCockpitBehoerde] = useState<CockpitBehoerde[]>([]);
  const [cockpitFaellig, setCockpitFaellig] = useState<CockpitFaellig[]>([]);
  const [cockpitFertig, setCockpitFertig] = useState<CockpitFertig[]>([]);
  const [cockpitAbgelaufen, setCockpitAbgelaufen] = useState<CockpitAbgelaufen[]>([]);
  const [cockpitFristen, setCockpitFristen] = useState<Record<string, string>>({});
  const [neuerAdmin, setNeuerAdmin] = useState('');
  // Ausweis-/Prüf-Modal: 'freigeben' = kombinierte Neuanlage+Freigabe
  // (Erstantrag, MRZ-Sichtprüfung mit Bestätigungs-Knopf), 'pruefen' =
  // reine Ansicht des Datensatzes (alle Antragsarten, jederzeit).
  const [ausweisAntrag, setAusweisAntrag] = useState<PendingAntrag | null>(null);
  const [ausweisData, setAusweisData] = useState<AntragAusweis | null>(null);
  const [ausweisLaden, setAusweisLaden] = useState(false);
  const [ausweisModus, setAusweisModus] = useState<'pruefen' | 'freigeben'>('pruefen');

  // Alle Quellen PARALLEL holen und jedes Ergebnis sofort in den State
  // schreiben — Anträge/Aufträge stehen nach Millisekunden, das Cockpit
  // (USP-Aufrufe) trudelt nach. Nach Aktionen bleibt das Board sichtbar
  // („aktualisiere…" im Kopf) statt bei jedem Klick komplett zu verschwinden.
  const load = useCallback(async () => {
    setRefreshing(true);
    const token = keycloak.token;
    const teile: Promise<void>[] = [
      listAntraege(token, 'alle').then((r) => setAntraege(r.antraege)),
      Promise.all([
        listFreigabeAuftraege(token, 'wartet_admin'),
        listFreigabeAuftraege(token, 'geplant'),
      ]).then(([wartend, geplant]) => setAuftraege([...wartend.auftraege, ...geplant.auftraege])),
      listPendingDokumente(token).then((r) => setDokumente(r.dokumente)),
      getAudit(token, 150).then((r) => setAuditLog(r.eintraege)),
      listAdmins(token).then((r) => { setAdmins(r.admins); setEnvAdmins(r.env_admins); }),
      listBlockierte(token).then((r) => setBlockierte(r.blockierte)),
      getCockpit(token).then((cp) => {
        setCockpitBehoerde(cp.behoerde);
        setCockpitFaellig(cp.faellig);
        setCockpitFertig(cp.fertig || []);
        setCockpitAbgelaufen(cp.abgelaufen || []);
        setCockpitFristen(cp.fristen || {});
      }),
    ];
    const ergebnisse = await Promise.allSettled(teile);
    const fehler = ergebnisse.find((e): e is PromiseRejectedResult => e.status === 'rejected');
    if (fehler) {
      setMsg({ variant: 'danger', text: 'Laden fehlgeschlagen: ' + ((fehler.reason as Error)?.message || String(fehler.reason)) });
    }
    setLoading(false);
    setRefreshing(false);
  }, [keycloak.token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleZip = async (a: PendingAntrag) => {
    try {
      const blob = await downloadAntragZip(keycloak.token, a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename || `antrag_${a.id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'ZIP-Download fehlgeschlagen: ' + (err as Error).message });
    }
  };

  // Prüf-Ansicht öffnen (nur ansehen, kein Freigabe-Knopf).
  const handlePruefen = useCallback(async (a: PendingAntrag) => {
    setAusweisModus('pruefen');
    setAusweisAntrag(a);
    setAusweisData(null);
    setAusweisLaden(true);
    try {
      const data = await getAntragAusweis(keycloak.token, a.id);
      setAusweisData(data);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Antrags-Daten konnten nicht geladen werden: ' + (err as Error).message });
      setAusweisAntrag(null);
    } finally {
      setAusweisLaden(false);
    }
  }, [keycloak.token]);

  // PDF (z.B. Strafregisterauszug) in neuem Tab öffnen.
  const handleOpenDatei = async (name: string) => {
    const a = ausweisAntrag;
    if (!a) return;
    try {
      const blob = await getAntragDatei(keycloak.token, a.id, name);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Datei konnte nicht geöffnet werden: ' + (err as Error).message });
    }
  };

  const handleStatus = async (a: PendingAntrag, status: string) => {
    setBusyId(a.id);
    setMsg(null);
    try {
      await setAntragStatus(keycloak.token, a.id, status);
      setMsg({ variant: 'success', text: `Antrag #${a.id} → ${status}` });
      await load();
      // Beim Übergang „In Prüfung" direkt den Datensatz zur Sichtprüfung
      // zeigen (persönliche Daten, Dokumente samt Nummern, Bilder).
      if (status === 'in_pruefung') await handlePruefen(a);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Statuswechsel fehlgeschlagen: ' + (err as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const handleFreigabe = async (a: PendingAntrag, force: boolean) => {
    setBusyId(a.id);
    setMsg(null);
    try {
      const result = await antragFreigeben(keycloak.token, a.id, { forceZeitfenster: force });
      const fId = (result.freigabe as any)?.f_id;
      if ((result as any).uebermittelt) {
        // In der Betriebszeit sofort durchgezogen: liegt jetzt bei der Behörde.
        setMsg({ variant: 'success', text: `✅ Zur behördlichen Prüfung freigegeben (f_id ${fId}) — Antrag #${a.id}. Er liegt jetzt unter „In Bearbeitung" (bei der Behörde).` });
      } else if (result.geplant) {
        const slot = result.uebermittlung_fruehestens
          ? new Date(result.uebermittlung_fruehestens).toLocaleString('de-AT', { dateStyle: 'medium', timeStyle: 'short' })
          : 'zum nächsten Behörden-Fenster';
        setMsg({ variant: 'success', text: `✅ Freigabe eingeplant (Antrag #${a.id}) — wird automatisch zur Prüfung freigegeben (frühestens ${slot}). Kein weiteres Zutun nötig.` });
      } else {
        setMsg({ variant: 'success', text: `✅ Zur behördlichen Prüfung freigegeben (f_id ${fId}) — Antrag #${a.id}` });
      }
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Freigabe abgelehnt: ' + (err as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const handleBmimi = async (a: PendingAntrag) => {
    setBusyId(a.id);
    try {
      await apiFetch(`/api/admin/antraege/${a.id}/bmimi-senden`, keycloak.token, { method: 'POST' });
      setMsg({ variant: 'success', text: `📤 Passkopie an die BMIMI gesendet (Antrag #${a.id})` });
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'BMIMI-Versand fehlgeschlagen: ' + (err as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  // Erstantrag ins USP anlegen (= übermitteln, KEINE Freigabe). Kostenneutral;
  // vergibt die p_id. Liefert `pid` zurück, damit der kombinierte Weg direkt
  // danach freigeben kann.
  const handleUebernehmen = async (a: PendingAntrag): Promise<number | null> => {
    setBusyId(a.id);
    setMsg(null);
    try {
      const r = await antragUebernehmen(keycloak.token, a.id);
      if (r.demo) {
        setMsg({ variant: 'warning', text: `🧪 Demo: Antrag #${a.id} wäre neu im USP angelegt worden (nicht persistiert).` });
        return null;
      }
      const offenTxt = r.offen && r.offen.length > 0 ? ` · offen: ${r.offen.join(', ')}` : '';
      setMsg({ variant: 'success', text: `📥 Im USP angelegt: p_id ${r.p_id} (Antrag #${a.id}) — Datensatz übermittelt, noch nicht freigegeben.${offenTxt}` });
      await load();
      return r.p_id ?? null;
    } catch (err) {
      setMsg({ variant: 'danger', text: 'USP-Neuanlage fehlgeschlagen: ' + (err as Error).message });
      return null;
    } finally {
      setBusyId(null);
    }
  };

  // Kombinierter Weg: erst den Ausweis-Check öffnen. Bestätigt der Admin dort,
  // läuft handleAusweisBestaetigen (anlegen -> freigeben).
  const handleUebernehmenUndFreigeben = async (a: PendingAntrag) => {
    setAusweisModus('freigeben');
    setAusweisAntrag(a);
    setAusweisData(null);
    setAusweisLaden(true);
    try {
      const data = await getAntragAusweis(keycloak.token, a.id);
      setAusweisData(data);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Ausweis-Daten konnten nicht geladen werden: ' + (err as Error).message });
      setAusweisAntrag(null);
    } finally {
      setAusweisLaden(false);
    }
  };

  const handleAusweisBestaetigen = async () => {
    const a = ausweisAntrag;
    if (!a) return;
    setAusweisAntrag(null);
    const pid = a.p_id ?? (await handleUebernehmen(a));
    if (pid == null) return; // Demo oder Fehler -> keine Freigabe
    await handleFreigabe({ ...a, p_id: pid }, false);
  };

  const handleJetzt = async (auf: FreigabeAuftrag) => {
    if (!window.confirm(`Freigabe für ${auf.name || auf.mgd_nummer} JETZT (vorzeitig) an die Behörde übermitteln?\nDie Kosten werden damit sofort ausgelöst.`)) return;
    try {
      const r = await freigabeAuftragJetzt(keycloak.token, auf.id);
      setMsg({ variant: 'success', text: r.demo ? `🧪 Demo: Auftrag #${auf.id} wäre jetzt zur Prüfung freigegeben worden (alle Guards ok).` : `✅ Zur behördlichen Prüfung freigegeben (f_id ${(r.freigabe as any)?.f_id}) — Auftrag #${auf.id}` });
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Sofort-Ausführung: ' + (err as Error).message });
    }
    await load();
  };

  const handleStorno = async (auf: FreigabeAuftrag) => {
    try {
      await stornoFreigabeAuftrag(keycloak.token, auf.id);
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Storno fehlgeschlagen: ' + (err as Error).message });
    }
  };

  const handleBestaetigen = async (auf: FreigabeAuftrag) => {
    try {
      await bestaetigeFreigabeAuftrag(keycloak.token, auf.id);
      setMsg({ variant: 'success', text: `Auftrag #${auf.id} bestätigt — der Scheduler gibt ihn ab ${auf.earliest_at ? moment(auf.earliest_at).format('DD.MM.YYYY') : 'sofort'} zur Prüfung frei.` });
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Bestätigen fehlgeschlagen: ' + (err as Error).message });
    }
  };

  const handleAufforderung = async (card: BoardCard) => {
    try {
      const res = await sendeAbgelaufenAufforderung(keycloak.token, card.pId!);
      setMsg({ variant: 'success', text: `Aufforderung an ${res.gesendet_an} gesendet.` });
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Aufforderung fehlgeschlagen: ' + (err as Error).message });
    }
  };

  const handleKontakt = async (card: BoardCard, art: KontaktArt, note?: string) => {
    try {
      await logKontakt(keycloak.token, card.pId!, art, note);
      setMsg({ variant: 'success', text: `Kontakt protokolliert: ${card.name}.` });
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Protokollieren fehlgeschlagen: ' + (err as Error).message });
    }
  };

  const board = buildBoard(antraege, auftraege, {
    behoerde: cockpitBehoerde, faellig: cockpitFaellig, fertig: cockpitFertig,
    abgelaufen: cockpitAbgelaufen, fristen: cockpitFristen,
  }, dokumente);
  const handleDokAnsehen = async (d: PendingDokument) => {
    try {
      const blob = await downloadPendingDokument(keycloak.token, d.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Anzeigen fehlgeschlagen: ' + (err as Error).message });
    }
  };
  const handleDokStatus = async (d: PendingDokument, status: string) => {
    try {
      await setPendingDokumentStatus(keycloak.token, d.id, status);
      await load();
    } catch (err) {
      setMsg({ variant: 'danger', text: 'Status setzen fehlgeschlagen: ' + (err as Error).message });
    }
  };
  // USP-Übernahme-Modal: Metadaten vom Admin nach der Sichtprüfung.
  const [uebDok, setUebDok] = useState<PendingDokument | null>(null);
  const [uebForm, setUebForm] = useState({ art: '2', nummer: '', ausstellung: '', gueltigBis: '', strafreg: '0', land: '' });
  const [uebBusy, setUebBusy] = useState(false);
  const [uebFehler, setUebFehler] = useState<string | null>(null);
  const handleDokUebernehmen = (d: PendingDokument) => {
    const note = d.note || '';
    const art = /strafregister|führungszeugnis/i.test(note) ? '0'
      : /reisepass/i.test(note) ? '1' : '2';
    const bis = (note.match(/gültig bis (\d{4}-\d{2}-\d{2})/) || [])[1] || '';
    // MRZ-Wächter-Befund vorbefüllen (automatisch gelesene Nummer/Ablauf).
    const mrz = note.match(/\[MRZ nummer=([A-Z0-9]+) bis=(\d{4}-\d{2}-\d{2})/);
    setUebForm({ art, nummer: mrz?.[1] || '', ausstellung: '', gueltigBis: mrz?.[2] || bis, strafreg: '0', land: '' });
    setUebFehler(null);
    setUebDok(d);
  };
  const handleUebAbsenden = async () => {
    if (!uebDok) return;
    setUebBusy(true);
    setUebFehler(null);
    try {
      const istSr = uebForm.art === '0';
      await uebernehmePendingDokument(keycloak.token, uebDok.id, {
        art: parseInt(uebForm.art),
        nummer: istSr ? null : uebForm.nummer.trim(),
        ausstellung: uebForm.ausstellung,
        gueltig_bis: !istSr && uebForm.gueltigBis ? uebForm.gueltigBis : null,
        strafreg: istSr ? parseInt(uebForm.strafreg) : 0,
        land: istSr ? uebForm.land.trim().toUpperCase() : null,
      });
      setUebDok(null);
      setMsg({ variant: 'success', text: '⬆ Dokument ins USP übernommen und Karte erledigt.' });
      await load();
    } catch (err) {
      setUebFehler((err as Error).message);
    } finally {
      setUebBusy(false);
    }
  };
  const actions: BoardActions = {
    zip: handleZip, pruefen: handlePruefen, status: handleStatus, freigabe: handleFreigabe,
    bmimi: handleBmimi, jetzt: handleJetzt, storno: handleStorno, bestaetigen: handleBestaetigen,
    uebernehmen: handleUebernehmen, uebernehmenUndFreigeben: handleUebernehmenUndFreigeben,
    aufforderung: handleAufforderung, kontakt: handleKontakt,
    dokAnsehen: handleDokAnsehen, dokStatus: handleDokStatus, dokUebernehmen: handleDokUebernehmen,
  };

  return (
    <Container fluid='md' className='py-4'>
      <h2 className='mb-1'>
        Anträge-Board
        {refreshing && !loading && (
          <span className='text-muted ms-3' style={{ fontSize: '0.85rem', fontWeight: 400 }}>
            <Spinner animation='border' size='sm' className='me-1' />aktualisiere…
          </span>
        )}
      </h2>
      <p className='text-muted mb-3' style={{ fontSize: '0.85rem' }}>
        Jeder Fall wandert von links nach rechts. <strong>Links</strong> = unser Bearbeitungsstand,
        <strong> „In Bearbeitung"</strong> = in unserer Prüfung oder bereits bei der Behörde (BMIMI entscheidet).
        Am Handy sind die Spalten untereinander gestapelt.
      </p>
      {msg && <Alert variant={msg.variant}>{msg.text}</Alert>}
      {loading ? <Spinner animation='border' /> : <KanbanBoard board={board} actions={actions} busyId={busyId} />}

      <h4 className='mt-4 mb-2'>Portal-Admins</h4>
      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Admins sehen die Admin-Kacheln und dürfen Anträge prüfen und Freigaben erteilen
        (jede Aktion läuft auf ihren Namen ins Audit-Log). Verwaltung liegt hier — kein
        Keycloak nötig.{envAdmins.length > 0 && <> Fest hinterlegt (Server-Konfig): {envAdmins.join(', ')}.</>}
      </p>
      <Table striped bordered size='sm' responsive style={{ maxWidth: 480 }}>
        <thead><tr><th>Mitgliedsnummer</th><th>ernannt von</th><th></th></tr></thead>
        <tbody>
          {admins.map((a) => (
            <tr key={a.mgd_nummer}>
              <td>{a.mgd_nummer}</td>
              <td>{a.added_by || '–'}</td>
              <td>
                <Button size='sm' variant='outline-danger' onClick={async () => {
                  try {
                    await removeAdmin(keycloak.token, a.mgd_nummer);
                    await load();
                  } catch (err) {
                    setMsg({ variant: 'danger', text: 'Entfernen fehlgeschlagen: ' + (err as Error).message });
                  }
                }}>Entfernen</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className='d-flex gap-2 mb-4' style={{ maxWidth: 480 }}>
        <input
          type='text'
          className='form-control form-control-sm'
          placeholder='Mitgliedsnummer (z.B. 585)'
          value={neuerAdmin}
          onChange={(e) => setNeuerAdmin(e.target.value)}
        />
        <Button size='sm' variant='primary' disabled={!neuerAdmin.trim()} onClick={async () => {
          try {
            await addAdmin(keycloak.token, neuerAdmin.trim());
            setNeuerAdmin('');
            await load();
          } catch (err) {
            setMsg({ variant: 'danger', text: 'Ernennen fehlgeschlagen: ' + (err as Error).message });
          }
        }}>Als Admin ernennen</Button>
      </div>

      <h4 className='mt-4 mb-2'>Für ZÜP gesperrte FO24-Konten</h4>
      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Operative Doppelnutzer (keine echten Personen): kein Matching, kein Portal-Zugriff,
        keine Anträge. Auch direkt aus der Matching-Review sperrbar.
      </p>
      {blockierte.length > 0 && (
        <Table striped bordered size='sm' responsive style={{ maxWidth: 640 }}>
          <thead><tr><th>Konto</th><th>Grund</th><th></th></tr></thead>
          <tbody>
            {blockierte.map((b) => (
              <tr key={b.mgd_nummer}>
                <td>{b.name ? `${b.name} (${b.mgd_nummer})` : b.mgd_nummer}</td>
                <td>{b.grund}</td>
                <td>
                  <Button size='sm' variant='outline-secondary'
                    onClick={() => setEntsperrKandidat(b)}>Entsperren…</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <AusweisCheckModal
        antrag={ausweisAntrag}
        data={ausweisData}
        laden={ausweisLaden}
        modus={ausweisModus}
        busy={ausweisAntrag != null && busyId === ausweisAntrag.id}
        onCancel={() => { setAusweisAntrag(null); setAusweisData(null); }}
        onConfirm={handleAusweisBestaetigen}
        onOpenDatei={handleOpenDatei}
      />

      <Modal show={uebDok !== null} onHide={() => !uebBusy && setUebDok(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⬆ Ins USP übernehmen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>
            <strong>{uebDok?.name ? `${uebDok.name} (${uebDok.mgd_nummer})` : uebDok?.mgd_nummer}</strong>
            {' — '}{uebDok?.filename}
          </p>
          <p className='text-muted' style={{ fontSize: '0.82rem' }}>
            Werte bitte vom gesichteten Dokument ablesen (🔍 Ansehen). Der Klick legt
            das Dokument im USP an, lädt die Datei hoch und erledigt die Karte.
          </p>
          <Form.Group className='mb-2'>
            <Form.Label>Dokumentart</Form.Label>
            <Form.Select value={uebForm.art} onChange={(e) => setUebForm({ ...uebForm, art: e.target.value })}>
              <option value='1'>Reisepass</option>
              <option value='2'>Personalausweis</option>
              <option value='3'>Fremdenpass</option>
              <option value='4'>Konventionspass</option>
              <option value='5'>Identitätsausweis</option>
              <option value='0'>Strafregisterauszug / Führungszeugnis</option>
            </Form.Select>
          </Form.Group>
          {uebForm.art !== '0' && (
            <Form.Group className='mb-2'>
              <Form.Label>Dokumentnummer</Form.Label>
              <Form.Control value={uebForm.nummer} onChange={(e) => setUebForm({ ...uebForm, nummer: e.target.value })} placeholder='z.B. PA1234567' />
            </Form.Group>
          )}
          <Form.Group className='mb-2'>
            <Form.Label>Ausstellungsdatum</Form.Label>
            <Form.Control type='date' value={uebForm.ausstellung} onChange={(e) => setUebForm({ ...uebForm, ausstellung: e.target.value })} />
          </Form.Group>
          {uebForm.art !== '0' ? (
            <Form.Group className='mb-2'>
              <Form.Label>Gültig bis (vom Dokument)</Form.Label>
              <Form.Control type='date' value={uebForm.gueltigBis} onChange={(e) => setUebForm({ ...uebForm, gueltigBis: e.target.value })} />
            </Form.Group>
          ) : (
            <>
              <Form.Group className='mb-2'>
                <Form.Label>Land des Auszugs (Wohnsitz-Zuordnung)</Form.Label>
                <Form.Control value={uebForm.land} onChange={(e) => setUebForm({ ...uebForm, land: e.target.value })} placeholder='z.B. DE, IT, CH' maxLength={2} />
              </Form.Group>
              <Form.Group className='mb-2'>
                <Form.Label>Eintragungen</Form.Label>
                <Form.Select value={uebForm.strafreg} onChange={(e) => setUebForm({ ...uebForm, strafreg: e.target.value })}>
                  <option value='0'>Nein</option>
                  <option value='1'>Ja</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
          {uebFehler && <Alert variant='danger' className='py-2'>{uebFehler}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' disabled={uebBusy} onClick={() => setUebDok(null)}>Abbrechen</Button>
          <Button variant='success' disabled={uebBusy || !uebForm.ausstellung || (uebForm.art !== '0' && !uebForm.nummer.trim()) || (uebForm.art === '0' && uebForm.land.trim().length !== 2)} onClick={handleUebAbsenden}>
            {uebBusy ? 'Übernimmt…' : '⬆ Übernehmen'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={entsperrKandidat !== null} onHide={() => setEntsperrKandidat(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Konto wirklich entsperren?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{entsperrKandidat?.name
              ? `${entsperrKandidat.name} (${entsperrKandidat.mgd_nummer})`
              : entsperrKandidat?.mgd_nummer}</strong>
          </p>
          <p className='mb-1'>Sperrgrund: <em>{entsperrKandidat?.grund || '—'}</em></p>
          <p className='text-muted mb-0' style={{ fontSize: '0.85rem' }}>
            Nach dem Entsperren nimmt das Konto wieder an Matching, Erinnerungen
            und Portal-Zugriff teil. Operative DA-42-Konten sollen gesperrt bleiben.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setEntsperrKandidat(null)}>Abbrechen</Button>
          <Button variant='danger' onClick={async () => {
            const b = entsperrKandidat;
            setEntsperrKandidat(null);
            if (!b) return;
            try {
              await removeBlockiert(keycloak.token, b.mgd_nummer);
              await load();
            } catch (err) {
              setMsg({ variant: 'danger', text: 'Entsperren fehlgeschlagen: ' + (err as Error).message });
            }
          }}>Ja, entsperren</Button>
        </Modal.Footer>
      </Modal>
      <div className='d-flex gap-2 mb-4' style={{ maxWidth: 640 }}>
        <input type='text' className='form-control form-control-sm' style={{ maxWidth: 130 }}
          placeholder='Mitgliedsnr.' value={neuBlock} onChange={(e) => setNeuBlock(e.target.value)} />
        <input type='text' className='form-control form-control-sm'
          placeholder='Grund' value={neuBlockGrund} onChange={(e) => setNeuBlockGrund(e.target.value)} />
        <Button size='sm' variant='outline-danger' disabled={!neuBlock.trim()} onClick={async () => {
          try {
            await addBlockiert(keycloak.token, neuBlock.trim(), neuBlockGrund.trim());
            setNeuBlock('');
            await load();
          } catch (err) {
            setMsg({ variant: 'danger', text: 'Sperren fehlgeschlagen: ' + (err as Error).message });
          }
        }}>Für ZÜP sperren</Button>
      </div>

      <h4 className='mt-4 mb-2'>Aktivitäts-Log</h4>
      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Wer hat sich angemeldet, was eingereicht, bestätigt oder freigegeben (neueste zuerst, letzte 150).
      </p>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        <Table striped bordered size='sm' responsive>
          <thead><tr><th>Zeit</th><th>Wer</th><th>Aktion</th><th>Detail</th></tr></thead>
          <tbody>
            {auditLog.map((e) => (
              <tr key={e.id}>
                <td className='text-nowrap'>{e.at ? moment(e.at).format('DD.MM. HH:mm') : '–'}</td>
                <td>{e.actor_name ? `${e.actor_name} (${e.actor})` : e.actor}</td>
                <td>{e.action}</td>
                <td style={{ fontSize: '0.8rem' }}>{[e.mgd_nummer && (e.name ? `${e.name} (${e.mgd_nummer})` : `Mitglied ${e.mgd_nummer}`), e.p_id && `p_id ${e.p_id}`, e.detail].filter(Boolean).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <p className='text-muted' style={{ fontSize: '0.85rem' }}>
        Ablauf: ZIP prüfen (MRZ-Abgleich!) → Daten nach USP übernehmen → „Importiert" →
        „Freigeben". Die Freigabe hat Server-Guards: Doppel-Freigabe-Sperre (10 Monate),
        Offenliste, Zeitfenster (40 Tage, übersteuerbar), nur FZT-Personen.
      </p>
    </Container>
  );
}

const GESCHLECHT_TXT: Record<string, string> = { '0': 'männlich', '1': 'weiblich', '2': 'divers' };
// dokinfo-Konvention (verifiziert): 0=Strafregister, 1=Reisepass, 2=Personalausweis.
const DOK_ART_TXT: Record<string, string> = { '0': 'Strafregister', '1': 'Reisepass', '2': 'Personalausweis' };

/**
 * Sichtprüfung des Antrags-Datensatzes. Zwei Modi:
 * - 'pruefen': reine Ansicht (alle Antragsarten) — persönliche Daten,
 *   alle Dokumente samt Nummern, Bilder, PDFs, Änderungsprotokoll. Öffnet
 *   automatisch beim Übergang „In Prüfung" und über den 🔍-Button.
 * - 'freigeben': Erstantrag-MRZ-Check mit „Anlegen + Freigeben"-Knopf
 *   (löst Kosten aus).
 */
function AusweisCheckModal({
  antrag, data, laden, modus, busy, onCancel, onConfirm, onOpenDatei,
}: {
  antrag: PendingAntrag | null;
  data: AntragAusweis | null;
  laden: boolean;
  modus: 'pruefen' | 'freigeben';
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onOpenDatei: (name: string) => void;
}) {
  const p = (data?.person ?? {}) as Record<string, unknown>;
  const val = (k: string) => (p[k] == null || p[k] === '' ? '–' : String(p[k]));
  const zeilen: Array<[string, string]> = [
    ['Vorname', val('vorname')],
    ['Familienname', val('famname')],
    ['Geburtsname', p.gebname ? val('gebname') : val('famname_1')],
    ...([p.famname_2, p.famname_3, p.famname_4, p.famname_5].some(Boolean)
      ? [['Weitere frühere Familiennamen', [p.famname_2, p.famname_3, p.famname_4, p.famname_5].filter(Boolean).join(', ')] as [string, string]]
      : []),
    ...(p.vorname_1 ? [['Früherer Vorname', val('vorname_1')] as [string, string]] : []),
    ['Geschlecht', GESCHLECHT_TXT[String(p.geschlecht)] ?? val('geschlecht')],
    ['Geburtsdatum', val('gebdatum')],
    ['Geburtsort', val('geb_ort')],
    ['Geburtsland', val('geb_ort_land')],
    ['Staat', val('staat_1')],
    ['E-Mail', val('kontakt_mail')],
    ['Telefon', val('kontakt_tel')],
  ];
  const dokumente = data?.dokumente ?? [];
  const bilder = data?.bilder ?? [];
  const pdfs = data?.pdfs ?? [];
  return (
    <Modal show={antrag !== null} onHide={onCancel} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          {modus === 'freigeben' ? '🛂 Ausweis-Check' : '🔍 Antrag prüfen'} — {antrag?.name || antrag?.mgd_nummer}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modus === 'freigeben' ? (
          <Alert variant='warning' className='py-2' style={{ fontSize: '0.85rem' }}>
            Bitte die Identitätsdaten <strong>gegen das Ausweisbild</strong> prüfen (MRZ-Regeln:
            nur erster Vorname, vollständiger Familienname inkl. Präfix, Geburtsort exakt wie am
            Dokument). Bestätigen legt die Person neu im USP an <strong>und</strong> gibt sie zur
            behördlichen Prüfung frei — das löst Kosten aus.
          </Alert>
        ) : (
          <Alert variant='info' className='py-2' style={{ fontSize: '0.85rem' }}>
            Sichtprüfung: Identitätsdaten gegen das Dokumentbild abgleichen (MRZ-Regeln), Dokument-
            nummern kontrollieren. Diese Ansicht ist nur lesend — Aktionen laufen über die Karte.
          </Alert>
        )}
        {laden && <div className='text-center py-4'><Spinner animation='border' /></div>}
        {!laden && data && (
          <>
            <div className='row g-3'>
              <div className='col-md-6'>
                <Table size='sm' borderless className='mb-0'>
                  <tbody>
                    {zeilen.map(([k, v]) => (
                      <tr key={k}><td className='text-muted' style={{ width: '42%' }}>{k}</td><td className='fw-semibold'>{v}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className='col-md-6'>
                {dokumente.length > 0 ? (
                  <Table size='sm' bordered className='mb-2' style={{ fontSize: '0.82rem' }}>
                    <thead><tr><th>Dokument</th><th>Nummer</th><th>ausgestellt</th></tr></thead>
                    <tbody>
                      {dokumente.map((d, i) => (
                        <tr key={i}>
                          <td>{DOK_ART_TXT[String(d.art)] ?? `Art ${d.art ?? '–'}`}</td>
                          <td className='fw-semibold'>{d.nummer || '—'}</td>
                          <td>{d.ausstellung || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className='text-muted small mb-2'>Keine Dokument-Angaben im Antrag.</div>
                )}
                {pdfs.length > 0 && (
                  <div className='d-flex flex-wrap gap-1 mb-2'>
                    {pdfs.map((n) => (
                      <Button key={n} size='sm' variant='outline-secondary' onClick={() => onOpenDatei(n)}>
                        📄 {n}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {data.aenderungen && (
              <pre className='mt-2 mb-2 p-2 bg-light border rounded' style={{ fontSize: '0.75rem', maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                {data.aenderungen}
              </pre>
            )}
            {bilder.length > 0 ? (
              <div className='row g-2 mt-1'>
                {bilder.map((b) => (
                  <div key={b.name} className='col-md-6 text-center'>
                    <img
                      src={`data:${b.mime};base64,${b.b64}`}
                      alt={b.name}
                      style={{ maxWidth: '100%', maxHeight: 360, border: '1px solid #ccc', borderRadius: 4 }}
                    />
                    <div className='text-muted' style={{ fontSize: '0.75rem' }}>{b.name}</div>
                  </div>
                ))}
              </div>
            ) : data.bild_base64 ? (
              <div className='text-center mt-2'>
                <img
                  src={`data:${data.bild_mime};base64,${data.bild_base64}`}
                  alt='Ausweis'
                  style={{ maxWidth: '100%', maxHeight: 360, border: '1px solid #ccc', borderRadius: 4 }}
                />
              </div>
            ) : pdfs.length === 0 && (
              <div className='text-muted small py-3'>
                Keine Bildvorschau möglich{data.dokname ? ` (${data.dokname})` : ''}.
                Bitte das Dokument über „ZIP" herunterladen und ansehen.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {modus === 'freigeben' ? (
          <>
            <Button variant='outline-secondary' onClick={onCancel} disabled={busy}>Abbrechen</Button>
            <Button variant='success' onClick={onConfirm} disabled={busy || laden}>
              {busy ? <Spinner size='sm' animation='border' /> : '🛂 Anlegen + Freigeben'}
            </Button>
          </>
        ) : (
          <Button variant='secondary' onClick={onCancel}>Schließen</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AntraegeAdmin;
